// Backend implementation of Document Upload Repository

import DocumentUploadRepository from "@/core/customers/repositories/DocumentUploadRepository";
import {
  DocumentUploadRequest,
  DocumentUploadResponse,
  DocumentsListResponse,
  DeleteDocumentRequest,
  DeleteDocumentResponse,
} from "@/core/customers/entities/DocumentUpload";
import {
  ApiDocumentUploadResponse,
  ApiDeleteDocumentResponse,
} from "@/core/customers/entities/ApiDocumentUpload";
import {
  uploadDocumentUrl,
  getDocumentsUrl,
  deleteDocumentUrl,
} from "../../../api/documentUploadEndpoints/documentUploadEndpoints";
import { ApiResponse } from "../../../api/types/ApiResponse";
import { ApiResponseHandler } from "../../../http/services/handleApiResponse/handleApiResponse";
import { HttpClient } from "../../../http/HttpClient";
import ViteEnv from "@/details/env/vite/ViteEnv";
import EnvKeys from "@/core/env/EnvKeys";
import { useAuthStore } from "@/core/stores/authStore";
import {
  BackendDocumentType,
  mapBackendTypeToUiIds,
} from "@/core/customers/mappers/documentTypeMapper";

export default class DocumentUploadRepositoryBackend implements DocumentUploadRepository {
  constructor(private readonly httpClient: HttpClient) {}

  async uploadDocument(
    request: DocumentUploadRequest,
  ): Promise<DocumentUploadResponse> {
    try {
      // Create FormData for file upload (new API contract)
      const formData = new FormData();
      formData.append("type", request.type);
      formData.append("documentName", request.name);
      formData.append("file", request.file);

      // Prefer native fetch for FormData to avoid any forced JSON headers
      const env = new ViteEnv();
      const baseUrl = env.get(EnvKeys.BACKEND_BASE_URL);
      const path = uploadDocumentUrl(request.customerId || "");

      const finalUrl = baseUrl
        ? `${String(baseUrl).replace(/\/$/, "")}/${path.replace(/^\//, "")}`
        : path;

      const token = useAuthStore.getState().user?.token;

      const response = await fetch(finalUrl, {
        method: "POST",
        body: formData,
        headers: token ? { "X-App-Token": `Bearer ${token}` } : undefined,
      });

      // Parse JSON and pass the typed ApiResponse to the handler
      const json = (await response.json()) as ApiDocumentUploadResponse;
      const result = ApiResponseHandler.handleResponse(json);

      if (result.success && result.data) {
        const apiData = result.data;

        // Handle both old backend types and new API codes
        // If it's a known BackendDocumentType, map it to UI id
        // Otherwise, use the API code directly (for new document types)
        let mappedDocumentType: string;
        try {
          const mappedIds = mapBackendTypeToUiIds(
            apiData.documentType as BackendDocumentType,
          );
          if (mappedIds && mappedIds.length > 0) {
            // Old backend type - use mapped UI id
            mappedDocumentType = mappedIds[0];
          } else {
            // New API code - use it directly
            mappedDocumentType = apiData.documentType;
          }
        } catch {
          // Fallback: use API code directly if mapping fails
          mappedDocumentType = apiData.documentType;
        }

        const documentResponse: DocumentUploadResponse = {
          success: true,
          message: result.message || "Document uploadé avec succès",
          data: {
            id: apiData.id,
            documentType: mappedDocumentType,
            documentName: apiData.documentName,
            fileName: apiData.fileName,
            objectKey: apiData.objectKey,
            contentType: apiData.contentType,
            size: apiData.size,
          },
        };

        return documentResponse;
      } else {
        return {
          success: false,
          message: result.message || "Erreur lors de l'upload du document",
        };
      }
    } catch (error) {
      console.error("📄 DocumentUploadRepositoryBackend: Upload error", error);
      return {
        success: false,
        message: "Erreur réseau lors de l'upload du document",
      };
    }
  }

  async getDocuments(customerId: string): Promise<DocumentsListResponse> {
    try {
      const response = await this.httpClient.get<
        ApiResponse<
          | Array<{
              id: string;
              documentType: string;
              documentName: string;
              fileName: string;
              objectKey: string;
              contentType: string;
              size: number;
            }>
          | {
              documents: Array<{
                id: string;
                documentType: string;
                documentName: string;
                fileName: string;
                objectKey: string;
                contentType: string;
                size: number;
              }>;
              totalCount: number;
            }
        >
      >({
        url: getDocumentsUrl(customerId),
        withAuth: true,
      });

      // Handle the standardized API response (supports array payloads too)
      const result = ApiResponseHandler.handleResponse(response);

      if (result.success && result.data) {
        const data = result.data;
        const rawItems = Array.isArray(data) ? data : data.documents;
        const totalCount = Array.isArray(data)
          ? rawItems.length
          : (data.totalCount ?? rawItems.length);

        // Map API items to internal Document entity
        const mappedItems = rawItems.map((it) => {
          // Handle both old backend types and new API codes
          // If it's a known BackendDocumentType, map it to UI id
          // Otherwise, use the API code directly (for new document types)
          let documentType: string;
          try {
            const mappedIds = mapBackendTypeToUiIds(
              it.documentType as BackendDocumentType,
            );
            documentType =
              mappedIds && mappedIds.length > 0
                ? mappedIds[0] // Old backend type - use mapped UI id
                : it.documentType; // New API code - use it directly
          } catch {
            // Fallback: use API code directly if mapping fails
            documentType = it.documentType;
          }

          return {
            id: it.id,
            name: it.documentName,
            type: documentType,
            url: "",
            createdAt: new Date().toISOString(),
            size: it.size,
            mimeType: it.contentType,
            fileName: it.fileName, // Preserve fileName from API response
          };
        });

        const documentsResponse: DocumentsListResponse = {
          success: true,
          message:
            (response as ApiResponse<typeof data>).message ||
            "Documents récupérés avec succès",
          data: mappedItems,
          totalCount,
        };

        return documentsResponse;
      } else {
        return {
          success: false,
          message:
            result.message || "Erreur lors de la récupération des documents",
          data: [],
          totalCount: 0,
        };
      }
    } catch (error) {
      console.error(
        "📄 DocumentUploadRepositoryBackend: Get documents error",
        error,
      );
      return {
        success: false,
        message: "Erreur réseau lors de la récupération des documents",
        data: [],
        totalCount: 0,
      };
    }
  }

  async deleteDocument(
    request: DeleteDocumentRequest,
  ): Promise<DeleteDocumentResponse> {
    try {
      const response = await this.httpClient.delete<
        ApiResponse<ApiDeleteDocumentResponse>
      >({
        url: deleteDocumentUrl(request.customerId, request.documentId),
        withAuth: true,
      });

      // Handle the standardized API response
      const result = ApiResponseHandler.handleResponse(response);

      if (result.success && result.data) {
        const deleteResponse: DeleteDocumentResponse = {
          success: true,
          message: result.data.message || "Document supprimé avec succès",
        };

        return deleteResponse;
      } else {
        return {
          success: false,
          message:
            result.message || "Erreur lors de la suppression du document",
        };
      }
    } catch (error) {
      console.error(
        "📄 DocumentUploadRepositoryBackend: Delete document error",
        error,
      );
      // Un refus metier arrive ici : le client HTTP releve les 4xx en exception, et pose le
      // message du serveur sur l'erreur. Sans le lire, l'agent verrait « erreur reseau » alors que
      // le serveur lui explique quoi faire - par exemple qu'il doit deposer la piece de
      // remplacement avant de retirer la derniere piece d'identite du client.
      const messageServeur = (error as { message?: unknown })?.message;
      if (typeof messageServeur === "string" && messageServeur.trim() !== "") {
        return { success: false, message: messageServeur };
      }
      return {
        success: false,
        message: "Erreur réseau lors de la suppression du document",
      };
    }
  }

}
